import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import spotlightService from '../services/SpotlightService';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';
import ChargifyPaymentForm from './ChargifyPaymentForm';
import './CheckoutPage.css';
import { APP_CONFIG } from '../config/AppConfig';
import { flattenBatchProducts } from '../utils/productUtils';
import { shouldShowProductDuration } from '../utils/productDisplay';

const CheckoutPage = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  
  const [channel, setChannel] = useState(null);
  const [parentChannel, setParentChannel] = useState(null); // Parent channel data for series
  const [products, setProducts] = useState([]);
  const [allRelatedProducts, setAllRelatedProducts] = useState([]); // Products from all related channels
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChargify, setShowChargify] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [hasPaymentProfile, setHasPaymentProfile] = useState(false);
  const [checkingPaymentProfile, setCheckingPaymentProfile] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null); // For getting dspro_id

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: `/checkout/${channelId}`,
        },
      });
    }
  }, [isAuthenticated, loginWithRedirect, channelId]);

  // Check if user has existing payment profile
  useEffect(() => {
    const checkPaymentProfile = async () => {
      if (!isAuthenticated) return;

      try {
        setCheckingPaymentProfile(true);
        console.log('💳 CheckoutPage: Checking for existing payment profile...');
        
        const hasProfile = await spotlightService.hasPaymentProfile();
        setHasPaymentProfile(hasProfile);
        
        console.log('💳 CheckoutPage: Has payment profile:', hasProfile);
      } catch (error) {
        console.error('💳 CheckoutPage: Error checking payment profile:', error);
        setHasPaymentProfile(false);
      } finally {
        setCheckingPaymentProfile(false);
      }
    };

    checkPaymentProfile();
  }, [isAuthenticated]);

  // Fetch subscription data to get the correct dspro_id for purchase
  useEffect(() => {
    const checkSubscription = async () => {
      if (!channelId || !isAuthenticated) return;

      try {
        console.log('💳 CheckoutPage: Checking subscription to get dspro_id for:', channelId);
        const subData = await spotlightService.checkSubscriptionAccess(channelId);
        setSubscriptionData(subData);
        console.log('💳 CheckoutPage: Subscription data received:', {
          dspro_id: subData?.dspro_id,
          purchasable: subData?.purchasable,
          unlocked: subData?.unlocked
        });
      } catch (error) {
        console.error('💳 CheckoutPage: Error checking subscription:', error);
      }
    };

    checkSubscription();
  }, [channelId, isAuthenticated]);

  // Fetch channel and products data
  useEffect(() => {
    const fetchData = async () => {
      if (!channelId || !isAuthenticated) return;

      try {
        setLoading(true);
        setError(null);

        console.log('🛒 CheckoutPage: Fetching channel and products for:', channelId);
        console.log('🛒 CheckoutPage: Location state:', location.state);

        // Try to get channel data from navigation state first (passed from Buy Now button)
        let channelData = location.state?.channel;
        let channelSlug = channelData?.slug;
        
        console.log('🛒 CheckoutPage: Initial channel data from state:', channelData);
        console.log('🛒 CheckoutPage: Channel slug:', channelSlug);
        
        // IMPORTANT: If we have a slug, fetch fresh channel data to get dspro_id
        // The channel object from navigation state might not have dspro_id populated
        if (channelSlug) {
          try {
            console.log('🛒 CheckoutPage: Fetching fresh channel data using slug:', channelSlug);
            const channelResponse = await spotlightService.getChannelBySlug(channelSlug);
            const freshChannelData = channelResponse?.channels?.[0] || channelResponse;
            console.log('🛒 CheckoutPage: Fresh channel data from API:', freshChannelData);
            console.log('🛒 CheckoutPage: Fresh channel IDs:', {
              _id: freshChannelData?._id,
              dspro_id: freshChannelData?.dspro_id,
              slug: freshChannelData?.slug
            });
            
            // Merge fresh data with existing data, prioritizing fresh data
            if (freshChannelData) {
              channelData = { ...channelData, ...freshChannelData };
            }
          } catch (channelError) {
            console.error('🛒 CheckoutPage: Error fetching fresh channel data:', channelError);
          }
        } else if (!channelData) {
          // No channel data from state (e.g., after login redirect) - fetch using channelId
          console.log('🛒 CheckoutPage: No channel state available, fetching channel by ID:', channelId);
          try {
            // First try to get channel by ID using subscription check which returns dspro_id
            const subResponse = await spotlightService.checkSubscriptionAccess(channelId);
            console.log('🛒 CheckoutPage: Subscription response for channel lookup:', subResponse);
            
            // If subscription check gives us a dspro_id, we can use that
            if (subResponse?.dspro_id) {
              channelData = {
                _id: channelId,
                dspro_id: subResponse.dspro_id,
              };
              console.log('🛒 CheckoutPage: Built channel data from subscription:', channelData);
            }
          } catch (fetchError) {
            console.error('🛒 CheckoutPage: Error fetching channel data:', fetchError);
          }
        }

        console.log('🛒 CheckoutPage: Final channel data:', channelData);
        console.log('🛒 CheckoutPage: Final channel IDs:', {
          _id: channelData?._id,
          dspro_id: channelData?.dspro_id,
          slug: channelData?.slug
        });

        setChannel(channelData);

        // Fetch products for this channel and related channels (parent/children)
        try {
          // Build list of channel IDs to fetch products for
          const channelIdsToFetch = [];
          
          // Add current channel
          const currentChannelId = channelData?.dspro_id || channelId;
          if (currentChannelId) {
            channelIdsToFetch.push(currentChannelId);
          }
          
          // Get parent channel data if this is a child channel (season)
          const parentChannelData = location.state?.parentChannel;
          const parentChannelId = parentChannelData?.dspro_id || channelData?.parent_id;
          if (parentChannelId && !channelIdsToFetch.includes(parentChannelId)) {
            channelIdsToFetch.push(parentChannelId);
            console.log('🛒 CheckoutPage: Including parent channel:', parentChannelId);
            // Store parent channel data for display
            if (parentChannelData) {
              setParentChannel(parentChannelData);
            }
          }
          
          // Add child channels (seasons) if available
          const childChannels = location.state?.childChannels || channelData?.childchannels || [];
          childChannels.forEach(child => {
            const childId = child?.dspro_id || child?._id;
            if (childId && !channelIdsToFetch.includes(childId)) {
              channelIdsToFetch.push(childId);
            }
          });
          
          console.log('🛒 CheckoutPage: Fetching products for channel IDs:', channelIdsToFetch);
          
          let productsData = [];
          
          // Use batch endpoint if we have multiple channels
          if (channelIdsToFetch.length > 1) {
            try {
              console.log('🛒 CheckoutPage: Using batch getProductsByChannels endpoint');
              const productsResponse = await spotlightService.getProductsByChannels(channelIdsToFetch);
              console.log('🛒 CheckoutPage: Batch products response:', productsResponse);
              
              if (productsResponse?.success && productsResponse?.products) {
                // Batch response nests products per channel:
                // [{ channel_id, products: [...] }] - flatten before use
                productsData = flattenBatchProducts(productsResponse, {
                  currentChannelId,
                  currentTitle: channelData?.title || channelData?.displayTitle,
                  parentChannelId,
                  parentTitle:
                    parentChannelData?.title || parentChannelData?.displayTitle || 'Complete Series',
                });
                setAllRelatedProducts(productsData);
              }
            } catch (batchError) {
              // Batch endpoint might not be available yet, fall back to fetching each channel individually
              console.warn('🛒 CheckoutPage: Batch endpoint failed, falling back to individual fetches:', batchError.message);
            }
          }
          
          // Fallback: fetch products for each channel individually
          if (productsData.length === 0) {
            console.log('🛒 CheckoutPage: Using individual getProductsByChannel calls');
            
            // Fetch current channel products
            try {
              const currentResponse = await spotlightService.getProductsByChannel(currentChannelId);
              console.log('🛒 CheckoutPage: Current channel products response:', currentResponse);
              if (currentResponse?.products) {
                // Tag products with the channel they came from
                currentResponse.products.forEach(p => {
                  p._sourceChannelId = currentChannelId;
                  p._sourceChannelTitle = channelData?.title || channelData?.displayTitle;
                });
                productsData.push(...currentResponse.products);
              }
            } catch (err) {
              console.warn('🛒 CheckoutPage: Error fetching current channel products:', err.message);
            }
            
            // Also fetch parent channel products if we have a parent
            if (parentChannelId) {
              try {
                console.log('🛒 CheckoutPage: Fetching parent channel products for:', parentChannelId);
                const parentResponse = await spotlightService.getProductsByChannel(parentChannelId);
                console.log('🛒 CheckoutPage: Parent channel products response:', parentResponse);
                if (parentResponse?.products) {
                  // Tag products with the parent channel info
                  const parentTitle = parentChannelData?.title || parentChannelData?.displayTitle || 'Complete Series';
                  parentResponse.products.forEach(p => {
                    p._sourceChannelId = parentChannelId;
                    p._sourceChannelTitle = parentTitle;
                    p._isParentProduct = true;
                  });
                  productsData.push(...parentResponse.products);
                }
              } catch (err) {
                console.warn('🛒 CheckoutPage: Error fetching parent channel products:', err.message);
              }
            }
          }
          
          // Deduplicate products by _id
          const uniqueProducts = productsData.reduce((acc, product) => {
            const id = product._id || product.product_id;
            if (!acc.find(p => (p._id || p.product_id) === id)) {
              acc.push(product);
            }
            return acc;
          }, []);
          
          // Sort products: prioritize products for the current channel, then by price
          const sortedProducts = uniqueProducts.sort((a, b) => {
            // Products directly for this channel come first
            const aForCurrentChannel = a.channels?.includes(currentChannelId) || a.channel_id === currentChannelId;
            const bForCurrentChannel = b.channels?.includes(currentChannelId) || b.channel_id === currentChannelId;
            
            if (aForCurrentChannel && !bForCurrentChannel) return -1;
            if (!aForCurrentChannel && bForCurrentChannel) return 1;
            
            // Then sort by number of channels (bundles/series products come second)
            const aChannelCount = a.channels?.length || a.channels_count || 1;
            const bChannelCount = b.channels?.length || b.channels_count || 1;
            
            return aChannelCount - bChannelCount;
          });
          
          setProducts(sortedProducts);

          console.log('🛒 CheckoutPage: Parsed products data:', sortedProducts);
          console.log('🛒 CheckoutPage: Number of products:', sortedProducts.length);

          // Auto-select first product if only one available
          if (sortedProducts.length === 1) {
            setSelectedProduct(sortedProducts[0]._id || sortedProducts[0].product_id);
          }
        } catch (productsError) {
          console.error('🛒 CheckoutPage: Error fetching products:', productsError);
          
          // Check if it's a 404 or 403
          if (productsError.message?.includes('404')) {
            setError('This channel is not part of any products yet. Please configure products in the backend.');
          } else if (productsError.message?.includes('403')) {
            setError('Authentication required. Please log in to view products.');
          } else {
            setError(`Failed to load product information: ${productsError.message}`);
          }
        }

      } catch (err) {
        console.error('🛒 CheckoutPage: Error fetching data:', err);
        setError('Failed to load checkout information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [channelId, isAuthenticated]);

  // Handle product selection
  const handleProductSelect = (productId) => {
    setSelectedProduct(productId);
    setShowChargify(false); // Reset Chargify form when changing selection
  };

  // Handle proceed to payment (for new customers - show Chargify form)
  const handleProceedToPayment = () => {
    if (!selectedProduct) {
      alert('Please select a product to continue.');
      return;
    }

    console.log('🛒 CheckoutPage: Proceeding to payment with product:', selectedProduct);
    setPurchaseError(null);
    setShowChargify(true);
  };

  // Handle direct purchase (for existing customers with payment profile)
  const handleDirectPurchase = async () => {
    if (!selectedProduct) {
      alert('Please select a product to continue.');
      return;
    }

    console.log('💳 CheckoutPage: Direct purchase with existing payment profile');
    setIsProcessingPayment(true);
    setPurchaseError(null);

    try {
      // Get the selected product object
      const product = products.find(p => (p._id || p.product_id) === selectedProduct);
      
      if (!product || !product.chargify_id) {
        throw new Error('Invalid product selected - missing chargify_id');
      }

      // Get channel ID for individual channel products (TVOD)
      // Priority: channel.dspro_id (from fresh API fetch) > subscriptionData.dspro_id > product.channel_id > channelId (URL)
      // We fetch fresh channel data using slug which should include dspro_id
      const subscriptionChannelId = channel?.dspro_id || subscriptionData?.dspro_id || product?.channel_id || channelId;
      const productType = product?.product_type;
      console.log('💳 CheckoutPage: Subscribing to product:', product);
      console.log('💳 CheckoutPage: Product type:', productType);
      console.log('💳 CheckoutPage: Channel ID for subscription:', subscriptionChannelId);
      console.log('💳 CheckoutPage: Source - channel.dspro_id:', channel?.dspro_id, 'subscriptionData.dspro_id:', subscriptionData?.dspro_id, 'product.channel_id:', product?.channel_id);
      
      // Subscribe using existing payment profile
      // Pass channel_id and product_type for proper handling
      const result = await spotlightService.subscribeToProduct(
        product.chargify_id,
        subscriptionChannelId,
        productType
      );

      console.log('✅ CheckoutPage: Purchase successful:', result);
      
      // Check if user was already subscribed
      if (result.alreadySubscribed) {
        setPurchaseError('You already own this content. Redirecting to watch page...');
        setTimeout(() => {
          navigate(`/?channel=${channel.slug || channel._id}`, {
            state: { channel }
          });
        }, 2000);
        return;
      }
      
      // Refresh the subscription status for this channel
      console.log('🔄 CheckoutPage: Refreshing subscription status...');
      try {
        const updatedSubscriptionStatus = await spotlightService.checkSubscriptionAccess(
          channel.dspro_id || channel._id
        );
        console.log('✅ CheckoutPage: Updated subscription status:', updatedSubscriptionStatus);
        
        // Update the channel object with fresh subscription data
        channel.subscription_access = updatedSubscriptionStatus;
      } catch (refreshError) {
        console.warn('⚠️ CheckoutPage: Could not refresh subscription status:', refreshError);
      }
      
      // Clear appload cache and force refresh
      console.log('🔄 CheckoutPage: Clearing appload cache and refreshing...');
      try {
        // Clear both new and old cache prefixes for backward compatibility
        localStorage.removeItem('electricnow_cache_SPOTLIGHT_GET_APP_LOAD');
        localStorage.removeItem('electricnow_cache_APP_LOAD_DATA');
        localStorage.removeItem('chillfree_cache_SPOTLIGHT_GET_APP_LOAD');
        localStorage.removeItem('chillfree_cache_APP_LOAD_DATA');
        console.log('✅ CheckoutPage: Appload cache cleared');
        
        await spotlightService.getAppLoad(true);
        console.log('✅ CheckoutPage: Appload data refreshed');
      } catch (apploadError) {
        console.warn('⚠️ CheckoutPage: Could not refresh appload:', apploadError);
      }
      
      setPurchaseSuccess(true);
      
      // Redirect to watch page after success
      setTimeout(() => {
        console.log('🔄 CheckoutPage: Redirecting to watch page with updated channel data');
        navigate(`/?channel=${channel.slug || channel._id}`, {
          state: { channel } // Pass updated channel object
        });
        
        // Force a full page reload to ensure all data is fresh
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }, 2000);
      
    } catch (err) {
      console.error('❌ CheckoutPage: Purchase failed:', err);
      setPurchaseError(err.message || 'Purchase failed. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = async (chargifyToken) => {
    console.log('💳 CheckoutPage: Payment token received:', chargifyToken);
    console.log('💳 CheckoutPage: Token type:', typeof chargifyToken);
    console.log('💳 CheckoutPage: Token value:', JSON.stringify(chargifyToken));
    
    setIsProcessingPayment(true);
    setPurchaseError(null);

    try {
      // Get the selected product object
      const product = products.find(p => (p._id || p.product_id) === selectedProduct);
      
      if (!product || !product.chargify_id) {
        throw new Error('Invalid product selected - missing chargify_id');
      }

      console.log('💳 CheckoutPage: Processing purchase for product:', product);
      console.log('💳 CheckoutPage: Product chargify_id:', product.chargify_id);

      // Check if user has existing payment profile
      console.log('💳 CheckoutPage: Checking for existing payment profile...');
      const hasProfile = await spotlightService.hasPaymentProfile();
      console.log('💳 CheckoutPage: User has payment profile:', hasProfile);

      // Get channel ID for individual channel products (TVOD)
      // Priority: channel.dspro_id (from fresh API fetch) > subscriptionData.dspro_id > product.channel_id > channelId (URL)
      // We fetch fresh channel data using slug which should include dspro_id
      const subscriptionChannelId = channel?.dspro_id || subscriptionData?.dspro_id || product?.channel_id || channelId;
      const productType = product?.product_type;
      console.log('💳 CheckoutPage: Channel ID for subscription:', subscriptionChannelId);
      console.log('💳 CheckoutPage: Product type:', productType);
      console.log('💳 CheckoutPage: Source - channel.dspro_id:', channel?.dspro_id, 'subscriptionData.dspro_id:', subscriptionData?.dspro_id, 'product.channel_id:', product?.channel_id);

      let result;
      if (hasProfile) {
        // User has existing payment profile, just subscribe
        console.log('💳 CheckoutPage: Subscribing existing customer...');
        result = await spotlightService.subscribeToProduct(product.chargify_id, subscriptionChannelId, productType);
      } else {
        // New customer, create payment profile and subscribe
        console.log('💳 CheckoutPage: Creating payment profile and subscribing...');
        result = await spotlightService.createPaymentAndSubscribe(chargifyToken, product.chargify_id, subscriptionChannelId, productType);
      }

      console.log('✅ CheckoutPage: Purchase successful:', result);
      
      // CRITICAL: Clear ALL subscription caches BEFORE fetching fresh data
      // This ensures we don't get stale cached data
      console.log('🗑️ CheckoutPage: Clearing subscription caches after purchase...');
      spotlightService.clearSubscriptionCaches();
      
      // Refresh the client token to get updated entitlements
      console.log('🔄 CheckoutPage: Refreshing client token after purchase...');
      try {
        await spotlightService.refreshClientToken();
        console.log('✅ CheckoutPage: Client token refreshed');
      } catch (tokenError) {
        console.warn('⚠️ CheckoutPage: Could not refresh client token:', tokenError);
      }
      
      // Now fetch fresh subscription status (caches are cleared)
      console.log('🔄 CheckoutPage: Refreshing subscription status...');
      try {
        const updatedSubscriptionStatus = await spotlightService.checkSubscriptionAccess(
          channel.dspro_id || channel._id
        );
        console.log('✅ CheckoutPage: Updated subscription status:', updatedSubscriptionStatus);
        
        // Update the channel object with fresh subscription data
        channel.subscription_access = updatedSubscriptionStatus;
      } catch (refreshError) {
        console.warn('⚠️ CheckoutPage: Could not refresh subscription status:', refreshError);
      }
      
      // Force refresh appload data
      console.log('🔄 CheckoutPage: Refreshing appload data...');
      try {
        await spotlightService.getAppLoad(true);
        console.log('✅ CheckoutPage: Appload data refreshed');
      } catch (apploadError) {
        console.warn('⚠️ CheckoutPage: Could not refresh appload:', apploadError);
      }
      
      // Check if user was already subscribed
      if (result.alreadySubscribed) {
        setPurchaseError('You are already subscribed to this content! Redirecting...');
        setTimeout(() => {
          // Force full page reload to ensure fresh data
          window.location.href = `/?channel=${channel.slug}`;
        }, 2000);
      } else {
        setPurchaseSuccess(true);
        // Redirect to PDP modal after 2 seconds with full page reload
        setTimeout(() => {
          // Force full page reload to ensure fresh data
          window.location.href = `/?channel=${channel.slug}`;
        }, 2000);
      }

    } catch (err) {
      console.error('❌ CheckoutPage: Purchase failed:', err);
      console.error('❌ CheckoutPage: Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response,
      });
      setPurchaseError(err.message || 'Purchase failed. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  // Handle payment error
  const handlePaymentError = (errorMessage) => {
    console.error('❌ CheckoutPage: Payment error:', errorMessage);
    setPurchaseError(errorMessage);
    setIsProcessingPayment(false);
  };

  // Handle cancel payment
  const handleCancelPayment = () => {
    console.log('🛒 CheckoutPage: Payment cancelled');
    setShowChargify(false);
    setPurchaseError(null);
  };

  // Get channel image
  const getChannelImage = () => {
    if (!channel) return null;

    const imageUrl =
      channel.spotlight_poster ||
      channel.poster ||
      channel.thumb ||
      channel.image?.spotlight_poster ||
      channel.image?.poster ||
      channel.image?.thumb;

    if (!imageUrl) return null;

    return optimizeImageUrl(imageUrl, getResponsiveImagePreset('CHANNEL_HERO'));
  };

  // Loading state
  if (loading) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-loading">
            <div className="spinner"></div>
            <p>Loading checkout...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-error">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="error-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2>Error Loading Checkout</h2>
            <p>{error}</p>
            <button onClick={() => navigate(-1)} className="btn-back">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No products available
  if (products.length === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-error">
            <h2>No Products Available</h2>
            <p>This content is not currently available for purchase.</p>
            <button onClick={() => navigate(-1)} className="btn-back">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (purchaseSuccess) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-success">
            <div className="success-icon">✓</div>
            <h2>Purchase Successful!</h2>
            <p>Thank you for your purchase. You now have access to this content.</p>
            <p className="success-redirect">Redirecting you to watch now...</p>
          </div>
        </div>
      </div>
    );
  }

  const channelImage = getChannelImage();

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        {/* Header */}
        <div className="checkout-header">
          <button onClick={() => navigate(-1)} className="btn-back-header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon-back"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back
          </button>
          <h1>Complete Your Purchase</h1>
        </div>

        <div className="checkout-content">
          {/* Left Column: Channel Info */}
          <div className="checkout-left">
            {channelImage && (
              <div className="channel-poster">
                <img src={channelImage} alt={channel?.title} />
              </div>
            )}
            <div className="channel-info">
              <h2>{channel?.title}</h2>
              {channel?.description && (
                <p className="channel-description">{channel.description}</p>
              )}
              {channel?.genres && channel.genres.length > 0 && (
                <div className="channel-metadata">
                  <span className="metadata-label">Genres:</span>
                  <span>{channel.genres.join(', ')}</span>
                </div>
              )}
              {APP_CONFIG.FEATURES.SHOW_CONTENT_YEAR && channel?.year && (
                <div className="channel-metadata">
                  <span className="metadata-label">Year:</span>
                  <span>{channel.year}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Product Selection */}
          <div className="checkout-right">
            <div className="product-selection">
              <h3>Select a Product</h3>
              <p className="product-selection-subtitle">
                Choose how you'd like to access this content
              </p>

              <div className="products-list">
                {products.map((product, index) => {
                  const channelCount = product.channels?.length || product.channels_count || 1;
                  const isBundle = channelCount > 1;
                  const currentChannelId = channel?.dspro_id || channelId;
                  const isForCurrentChannel = product._sourceChannelId === currentChannelId ||
                                              product.channels?.includes(currentChannelId) || 
                                              product.channel_id === currentChannelId;
                  const isParentProduct = product._isParentProduct;
                  const isBestValue = isParentProduct && products.length > 1;
                  
                  // Use channel title for display instead of generic product name
                  // Priority: source channel title > channel title > product name
                  let displayTitle;
                  if (isForCurrentChannel) {
                    displayTitle = channel?.title || channel?.displayTitle || product._sourceChannelTitle || product.name;
                  } else if (isParentProduct) {
                    displayTitle = product._sourceChannelTitle || parentChannel?.title || 'Complete Series';
                  } else {
                    displayTitle = product._sourceChannelTitle || product.name;
                  }
                  
                  return (
                    <div
                      key={product._id || product.product_id}
                      className={`product-option ${
                        selectedProduct === (product._id || product.product_id) ? 'selected' : ''
                      } ${isBundle ? 'product-bundle' : ''} ${isParentProduct ? 'product-parent' : ''}`}
                      onClick={() => handleProductSelect(product._id || product.product_id)}
                    >
                      <div className="product-radio">
                        <input
                          type="radio"
                          name="product"
                          value={product._id || product.product_id}
                          checked={selectedProduct === (product._id || product.product_id)}
                          onChange={() => handleProductSelect(product._id || product.product_id)}
                        />
                      </div>
                      <div className="product-details">
                        <div className="product-header">
                          <h4>{displayTitle}</h4>
                          {isBestValue && (
                            <span className="product-badge">Best Value</span>
                          )}
                        </div>
                        {product.description && (
                          <p className="product-description">{product.description}</p>
                        )}
                        {isBundle && (
                          <p className="product-channels-count">
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon-bundle" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                              <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                            </svg>
                            Includes {channelCount} titles
                          </p>
                        )}
                        {isParentProduct && !isBundle && (
                          <p className="product-scope-note">Entire series access</p>
                        )}
                      </div>
                      <div className="product-price">
                        <span className="price">{product.price}</span>
                        {shouldShowProductDuration(product) && (
                          <span className="price-duration">
                            / {product.duration.number} {product.duration.unit}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Payment Section */}
              {checkingPaymentProfile ? (
                <div className="checking-payment-profile">
                  <div className="spinner"></div>
                  <p>Checking payment profile...</p>
                </div>
              ) : !showChargify ? (
                <>
                  {purchaseError && (
                    <div className="purchase-error-banner">
                      <span className="error-icon">⚠️</span>
                      <div>
                        <strong>Purchase Failed</strong>
                        <p>{purchaseError}</p>
                      </div>
                    </div>
                  )}
                  
                  {hasPaymentProfile ? (
                    // Existing customer - show direct buy button
                    <button
                      className="btn-proceed btn-buy-now"
                      onClick={handleDirectPurchase}
                      disabled={!selectedProduct || isProcessingPayment}
                    >
                      {isProcessingPayment ? (
                        <>
                          <span className="spinner-small"></span>
                          Processing...
                        </>
                      ) : (
                        'Buy Now'
                      )}
                    </button>
                  ) : (
                    // New customer - show proceed to payment button
                    <button
                      className="btn-proceed"
                      onClick={handleProceedToPayment}
                      disabled={!selectedProduct}
                    >
                      Proceed to Payment
                    </button>
                  )}
                </>
              ) : (
                <div className="chargify-form-container">
                  {purchaseError && (
                    <div className="purchase-error-banner">
                      <span className="error-icon">⚠️</span>
                      <div>
                        <strong>Purchase Failed</strong>
                        <p>{purchaseError}</p>
                      </div>
                    </div>
                  )}
                  
                  <ChargifyPaymentForm
                    product={products.find(p => (p._id || p.product_id) === selectedProduct)}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    onCancel={handleCancelPayment}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;

